import React, { createContext, useContext, useEffect, useState } from 'react'
import { authApi, setAuthTokens, clearAuthTokens, isAuthenticated } from '../utils/api'
import type { User } from '../types'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAdmin: boolean
  isRespondent: boolean
  login: (email: string, password: string) => Promise<void>
  loginWithTokens: (accessToken: string, sessionToken: string, email: string) => Promise<void>
  register: (email: string, password: string, fullName?: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // is_admin is true for OWNER/ADMIN roles, or when user has no memberships (new user)
  const isAdmin = user?.is_admin ?? false
  const hasRoles = (user?.roles?.length ?? 0) > 0
  const isRespondent = user ? (hasRoles && !isAdmin) : false

  const checkAuth = async () => {
    if (!isAuthenticated()) {
      setIsLoading(false)
      return
    }

    try {
      const userData = await authApi.getCurrentUser()
      setUser(userData)
    } catch (error) {
      console.error('Auth check failed:', error)
      clearAuthTokens()
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login({ email, password })
      setAuthTokens(response.access_token, response.session_token)
      // After login, fetch full user info (includes roles)
      const userData = await authApi.getCurrentUser()
      setUser(userData)
      // Save email to localStorage for organization member comparison
      localStorage.setItem('userEmail', email)
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    }
  }

  const loginWithTokens = async (accessToken: string, sessionToken: string, email: string) => {
    // Passwordless / OAuth: tokens already issued, just persist and hydrate the user.
    setAuthTokens(accessToken, sessionToken)
    const userData = await authApi.getCurrentUser()
    setUser(userData)
    localStorage.setItem('userEmail', email)
  }

  const register = async (email: string, password: string, fullName?: string) => {
    try {
      const response = await authApi.register({
        email,
        password,
        full_name: fullName
      })
      setAuthTokens(response.access_token, response.session_token)
      // After register, fetch full user info (includes roles)
      const userData = await authApi.getCurrentUser()
      setUser(userData)
      // Save email to localStorage for organization member comparison
      localStorage.setItem('userEmail', email)
    } catch (error) {
      console.error('Registration failed:', error)
      throw error
    }
  }

  const logout = async () => {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      clearAuthTokens()
      setUser(null)
    }
  }

  useEffect(() => {
    // Dev auto-login: ?email=user@example.com on any page
    const params = new URLSearchParams(window.location.search)
    const autoEmail = params.get('email')
    if (autoEmail && import.meta.env.DEV) {
      const autoLogin = async () => {
        try {
          await login(autoEmail, 'testpass123')
          params.delete('email')
          const clean = params.toString()
          window.history.replaceState({}, '', window.location.pathname + (clean ? '?' + clean : ''))
        } catch (err) {
          console.error('Auto-login failed:', err)
        } finally {
          setIsLoading(false)
        }
      }
      autoLogin()
    } else {
      checkAuth()
    }
  }, [])

  const value = {
    user,
    isLoading,
    isAdmin,
    isRespondent,
    login,
    loginWithTokens,
    register,
    logout,
    checkAuth,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
